import { css } from 'aphrodite';
import * as React from 'react';
import { ISupportGitHubIssueLink, ISupportGitHubIssueUpdateRequest } from '../../../models/Support/ISupportGitHub';
import { ISupportTicket } from '../../../models/Support/ISupportTicket';
import {
	useCreateGitHubIssueMutation,
	useLinkGitHubIssueLinkMutation,
	useUpdateGitHubIssueMutation,
} from '../../../queries/Support/useSupportGitHub';
import { useGenerateGitHubIssueMarkdownMutation } from '../../../queries/Support/useSupportTicketMutations';
import { getApiErrorMessage } from '../../../utils/Support/supportHelpers';
import { LoadingSpinner } from '../../../web/components/LoadingSpinner';
import { Button } from '../../../web/components-v2/Button';
import { Dialog, DialogFooter, DialogHeader, DialogPopup, DialogTitle } from '../../../web/components-v2/Dialog';
import { Input } from '../../../web/components-v2/Input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../web/components-v2/Select';
import { TextArea } from '../../../web/components-v2/TextArea';
import { supportSharedStyles as shared } from '../../components/supportShared/styles/supportSharedStyles';
import { buildAdminAccountUrl, buildAdminSupportTicketUrl } from '../../utils';
import { styles } from './styles';
import { getTicketContactLabel } from './supportTicketPresentation';

const GITHUB_ISSUE_REGEX = /^https:\/\/github\.com\/.+\/issues\/\d+$/;

export const LinkGitHubIssueModal: React.FC<{
	ticketId: string;
	onClose: () => void;
}> = ({ ticketId, onClose }) => {
	const [url, setUrl] = React.useState('');
	const [error, setError] = React.useState('');
	const linkMutation = useLinkGitHubIssueLinkMutation(ticketId);

	const handleLink = () => {
		const trimmed = url.trim();
		if (!GITHUB_ISSUE_REGEX.test(trimmed)) {
			setError('URL must match https://github.com/{owner}/{repo}/issues/{number}');
			return;
		}
		setError('');
		linkMutation.mutate(trimmed, {
			onSuccess: () => onClose(),
			onError: err => setError(getApiErrorMessage(err, 'Failed to link GitHub issue.')),
		});
	};

	return (
		<Dialog
			open
			onOpenChange={open => {
				if (!open) onClose();
			}}
		>
			<DialogPopup>
				<DialogHeader>
					<DialogTitle>Link GitHub Issue</DialogTitle>
				</DialogHeader>
				<div className={css(shared.modalBody)}>
					<div>
						<label className={css(shared.modalLabel)}>GitHub Issue URL</label>
						<Input
							styles={[shared.modalInput, error && shared.modalInputError]}
							type='text'
							placeholder='https://github.com/Real-Magic/Leviosa/issues/123'
							value={url}
							onChange={e => {
								setUrl(e.target.value);
								if (error) setError('');
							}}
							onKeyDown={e => {
								if (e.key === 'Enter') handleLink();
							}}
							autoFocus
						/>
						{error && <div className={css(shared.modalErrorText)}>{error}</div>}
					</div>
				</div>
				<DialogFooter>
					<Button variant='outline' size='sm' onClick={onClose}>
						Cancel
					</Button>
					<Button variant='primary' size='sm' onClick={handleLink} disabled={linkMutation.isPending}>
						{linkMutation.isPending ? 'Linking...' : 'Link'}
					</Button>
				</DialogFooter>
			</DialogPopup>
		</Dialog>
	);
};

type CreateIssueStep = 'choose' | 'describe' | 'generating' | 'review';

export const CreateGitHubIssueModal: React.FC<{
	ticketId: string;
	ticket: ISupportTicket;
	onClose: () => void;
}> = ({ ticketId, ticket, onClose }) => {
	const [step, setStep] = React.useState<CreateIssueStep>('choose');
	const [description, setDescription] = React.useState('');
	const [title, setTitle] = React.useState('');
	const [body, setBody] = React.useState('');
	const [createError, setCreateError] = React.useState('');

	const generateMutation = useGenerateGitHubIssueMarkdownMutation(ticketId);
	const createMutation = useCreateGitHubIssueMutation(ticketId);

	const buildContextLinks = () => {
		const lines: string[] = [];
		if (ticket.accountId) {
			lines.push(`**Account:** [${ticket.accountName || ticket.accountId}](${buildAdminAccountUrl(ticket.accountId)})`);
		}
		lines.push(`**Support Ticket:** [#${ticket.number}](${buildAdminSupportTicketUrl(ticketId)})`);
		return lines.join('\n');
	};

	const handleQuickCreate = () => {
		const quickTitle = `${getTicketContactLabel(ticket)}: ${ticket.subject}`;
		const quickBody = buildContextLinks();
		setCreateError('');
		createMutation.mutate(
			{ title: quickTitle, body: quickBody },
			{
				onSuccess: () => onClose(),
				onError: error => setCreateError(getApiErrorMessage(error, 'Failed to create GitHub issue.')),
			}
		);
	};

	const handleGenerate = () => {
		if (!description.trim()) return;
		setStep('generating');
		generateMutation.mutate(description.trim(), {
			onSuccess: data => {
				setTitle(data.title || `${getTicketContactLabel(ticket)}: ${ticket.subject}`);
				setBody(buildContextLinks() + '\n\n' + data.body);
				setStep('review');
			},
			onError: () => {
				setStep('describe');
			},
		});
	};

	const handleCreateIssue = () => {
		if (!title.trim()) return;
		setCreateError('');
		createMutation.mutate(
			{ title: title.trim(), body },
			{
				onSuccess: () => onClose(),
				onError: error => setCreateError(getApiErrorMessage(error, 'Failed to create GitHub issue.')),
			}
		);
	};

	return (
		<Dialog
			open
			onOpenChange={open => {
				if (!open) onClose();
			}}
		>
			<DialogPopup>
				<DialogHeader>
					<DialogTitle>Create GitHub Issue</DialogTitle>
				</DialogHeader>
				<div className={css(shared.modalBody)}>
					{step === 'choose' && (
						<>
							<div className={css(styles.ghChooseRow)}>
								<Button
									variant='outline'
									size='sm'
									onClick={handleQuickCreate}
									disabled={createMutation.isPending}
									styles={[styles.ghChooseBtn]}
								>
									{createMutation.isPending ? 'Creating...' : 'Quick Create'}
									<div className={css(styles.ghChooseDesc)}>Creates an issue through the API</div>
								</Button>
								<Button variant='outline' size='sm' onClick={() => setStep('describe')} styles={[styles.ghChooseBtn]}>
									AI-Assisted
									<div className={css(styles.ghChooseDesc)}>Generate a detailed issue with AI</div>
								</Button>
							</div>
							{createError && <div className={css(shared.modalErrorText)}>{createError}</div>}
						</>
					)}

					{step === 'describe' && (
						<>
							<div>
								<label className={css(shared.modalLabel)}>Describe the issue</label>
								<TextArea
									styles={[styles.ghTextarea]}
									placeholder='e.g. Contact import fails with missing email fields'
									value={description}
									onChange={e => setDescription(e.target.value)}
									autoFocus
								/>
							</div>
						</>
					)}

					{step === 'generating' && (
						<div className={css(shared.modalSpinnerRow)}>
							<LoadingSpinner type='small' />
							<span>Generating issue markdown...</span>
						</div>
					)}

					{step === 'review' && (
						<>
							<div>
								<label className={css(shared.modalLabel)}>Title</label>
								<Input
									styles={[shared.modalInput]}
									type='text'
									value={title}
									onChange={e => setTitle(e.target.value)}
								/>
							</div>
							<div>
								<label className={css(shared.modalLabel)}>Body (Markdown)</label>
								<TextArea
									styles={[styles.ghTextarea, styles.ghTextareaMono]}
									value={body}
									onChange={e => setBody(e.target.value)}
								/>
							</div>
							{createError && <div className={css(shared.modalErrorText)}>{createError}</div>}
						</>
					)}
				</div>
				<DialogFooter>
					{step === 'choose' && (
						<Button variant='outline' size='sm' onClick={onClose}>
							Cancel
						</Button>
					)}
					{step === 'describe' && (
						<>
							<Button variant='outline' size='sm' onClick={onClose}>
								Cancel
							</Button>
							<Button variant='primary' size='sm' onClick={handleGenerate} disabled={!description.trim()}>
								Generate
							</Button>
						</>
					)}
					{step === 'review' && (
						<>
							<Button variant='outline' size='sm' onClick={() => setStep('describe')}>
								Back
							</Button>
							<Button
								variant='primary'
								size='sm'
								onClick={handleCreateIssue}
								disabled={!title.trim() || createMutation.isPending}
							>
								{createMutation.isPending ? 'Creating...' : 'Create Issue'}
							</Button>
						</>
					)}
				</DialogFooter>
			</DialogPopup>
		</Dialog>
	);
};

export const GitHubUpdateIssueModal: React.FC<{
	ticketId: string;
	issue: ISupportGitHubIssueLink;
	onClose: () => void;
}> = ({ ticketId, issue, onClose }) => {
	const [title, setTitle] = React.useState(issue.title || '');
	const [body, setBody] = React.useState('');
	const [state, setState] = React.useState(issue.state === 'Closed' ? 'Closed' : 'Open');
	const [error, setError] = React.useState('');
	const mutation = useUpdateGitHubIssueMutation(ticketId, issue.id);

	const handleSubmit = () => {
		const payload: ISupportGitHubIssueUpdateRequest = {
			title: title.trim(),
			state,
		};
		if (body.trim()) payload.body = body.trim();

		setError('');
		mutation.mutate(payload, {
			onSuccess: () => onClose(),
			onError: err => setError(getApiErrorMessage(err, 'Failed to update GitHub issue.')),
		});
	};

	return (
		<Dialog open onOpenChange={open => !open && onClose()}>
			<DialogPopup>
				<DialogHeader>
					<DialogTitle>Update GitHub Issue</DialogTitle>
				</DialogHeader>
				<div className={css(shared.modalBody)}>
					<div className={css(styles.githubPanelMeta)}>
						{issue.repository}#{issue.number}
					</div>
					<div>
						<label className={css(shared.modalLabel)}>Title</label>
						<Input styles={[shared.modalInput]} value={title} onChange={e => setTitle(e.target.value)} autoFocus />
					</div>
					<div>
						<label className={css(shared.modalLabel)}>State</label>
						<Select value={state} onValueChange={setState}>
							<SelectTrigger>
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value='Open'>Open</SelectItem>
								<SelectItem value='Closed'>Closed</SelectItem>
							</SelectContent>
						</Select>
					</div>
					<div>
						<label className={css(shared.modalLabel)}>Body overwrite (optional)</label>
						<TextArea
							styles={[styles.ghTextarea, styles.ghTextareaMono]}
							placeholder='Leave blank to keep the existing GitHub body.'
							value={body}
							onChange={e => setBody(e.target.value)}
						/>
					</div>
					{error && <div className={css(shared.modalErrorText)}>{error}</div>}
				</div>
				<DialogFooter>
					<Button variant='outline' size='sm' onClick={onClose}>
						Cancel
					</Button>
					<Button variant='primary' size='sm' onClick={handleSubmit} disabled={!title.trim() || mutation.isPending}>
						{mutation.isPending ? 'Updating...' : 'Update Issue'}
					</Button>
				</DialogFooter>
			</DialogPopup>
		</Dialog>
	);
};
